import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { auth } from "../../../firebases";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { useUserContext } from "context/UserContext";
import getDefaultPathByRole from "utils/getDefaultPathByRole";
import { useTranslation } from "react-i18next";

function Basic() {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { setIdToken, setPosPidArray, setHierarchyData, fetchUserRole, fetchUserData } =
    useUserContext();
  const [loginMessage, setLoginMessage] = useState("");

  // pid to android_id conversion function
  const convertPidToAndroidId = (pid) => {
    const serialNumber = pid.slice(-5);
    return `VB09195D${serialNumber}`;
  };

  // Build hierarchy from flat list
  const buildHierarchy = (items, parentId = null) => {
    const children = items
      .filter((item) => String(item.parent || "") === String(parentId))
      .map((item) => ({
        ID: item.id,
        Name: item.name,
        NameAlias: item.note_private && item.note_private !== "" ? item.note_private : null,
        Parent: item.parent || "",
        Wallet: item.Wallet || null, // Keep for compatibility, though API may not provide Wallet
        Children: buildHierarchy(items, item.id),
      }));
    return children.length > 0 ? children : null;
  };

  const getFriendlyLoginError = (firebaseError) => {
    const code = firebaseError?.code || "";
    if (["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found"].includes(code)) {
      return t("errors.invalidCredential");
    }
    if (code === "auth/too-many-requests") {
      return t("errors.tooManyRequests");
    }
    return t("errors.default");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const db = getFirestore();
    try {
      // Firebase authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();
      setIdToken(idToken);
      localStorage.setItem("idToken", idToken);
      setLoginMessage(t("status.loggingIn"));

      // Fetch role and userData
      const [fetchedRole] = await Promise.all([fetchUserRole(email), fetchUserData(email)]);

      // Fetch hierarchy data from new API
      // const apiUrl = `https://crm-mqtt-718408119422.asia-east1.run.app/api/v1/mdm/thirdpartylist?email=${encodeURIComponent(
      //   email
      // )}`;

      let hierarchy = null;
      let dolibarrPosPidArray = [];

      // try {
      //   const response = await fetch(apiUrl, {
      //     method: "GET",
      //     headers: {
      //       "Mdm-Auth": "57b2a9ba-4428-48e4-b137-f8337b68595e",
      //     },
      //   });

      //   if (response.ok) {
      //     const result = await response.json();
      //     if (result.code === 200 && result.data && result.data.length > 0) {
      //       const thirdparties = result.data;
      //       const rootNodes = thirdparties.filter((item) => item.email === email);

      //       if (rootNodes.length) {
      //         const root = rootNodes[0];
      //         const matchedNode = {
      //           ID: root.id,
      //           Name: root.name,
      //           NameAlias: root.note_private && root.note_private !== "" ? root.note_private : null,
      //           Parent: root.parent || "",
      //           Wallet: root.Wallet || null,
      //           Children: buildHierarchy(thirdparties, root.id),
      //         };

      //         hierarchy = {
      //           Username: root.name,
      //           MatchedNode: matchedNode,
      //         };

      //         // 遍歷 hierarchy 收集 pos_pid
      //         const traverseNodes = (node) => {
      //           if (node?.Wallet?.posPid) {
      //             const pos_pid = node.Wallet.posPid;
      //             dolibarrPosPidArray.push({
      //               pos_pid,
      //               android_id: convertPidToAndroidId(pos_pid),
      //             });
      //           }
      //           if (node?.Children) {
      //             node.Children.forEach((child) => traverseNodes(child));
      //           }
      //         };
      //         traverseNodes(hierarchy.MatchedNode);
      //       }
      //     }
      //   }
      // } catch (err) {
      //   console.warn("Thirdparties API fetch failed, skipping hierarchy:", err.message);
      // }

      // if (hierarchy) {
      //   setHierarchyData(hierarchy);
      //   localStorage.setItem("hierarchyData", JSON.stringify(hierarchy));
      // }

      // Collect pos_pid from Firebase
      const firebasePosSnapshot = await getDocs(collection(db, "users", email, "pos"));
      const firebasePosData = firebasePosSnapshot.docs.map((doc) => doc.data());
      console.log("Firebase POS docs:", firebasePosData);
      const firebasePosPidArray = firebasePosData
        .map((data) => {
          if (data.license && data.pos_pid) {
            return {
              pos_pid: data.pos_pid,
              android_id: convertPidToAndroidId(data.pos_pid),
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      // Merge and deduplicate pos_pid arrays
      const mergedPosPidArray = [...firebasePosPidArray, ...dolibarrPosPidArray];
      const uniquePosPidArray = Array.from(
        new Map(mergedPosPidArray.map((item) => [item.pos_pid, item])).values()
      );
      console.log("Merged POS devices:", uniquePosPidArray);

      setPosPidArray(uniquePosPidArray);
      localStorage.setItem("posPidArray", JSON.stringify(uniquePosPidArray));

      const redirectPath = location.state?.from?.pathname || getDefaultPathByRole(fetchedRole);
      navigate(redirectPath, { replace: true });
      setLoginMessage("");
    } catch (error) {
      console.error("Login error:", error);
      setError(getFriendlyLoginError(error));
      setLoginMessage("");
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t("title")}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleLogin}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label={t("emailLabel")}
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label={t("passwordLabel")}
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            {error && (
              <MDTypography variant="body2" color="error" textAlign="center" mb={2}>
                {error}
              </MDTypography>
            )}
            <MDBox mt={4} mb={1}>
              <MDButton variant="gradient" color="info" fullWidth type="submit">
                {t("signInButton")}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                <MDTypography
                  component={Link}
                  to="/authentication/forget-password"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  {t("forgotPasswordLink")}
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
